import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';

import type { ConversationQuestion } from '../../types';
import {
  fillTemplate,
  formatDuration,
  formatTime,
  oneLine,
  unansweredLabel,
} from '../../utils/formatters';
import defaults from '../../translations/en.json';
import {
  HISTORY_NUMERIC_CLASS,
  isoComparator,
  numberComparator,
  viewColumn,
} from './historyColumnDefs';
import { getTokenColumnDefs, TokenCells } from './tokenColumnDefs';

type QuestionColumnDefsArgs = {
  onOpenQuestion: (row: ConversationQuestion) => void;
  smallWidget?: boolean;
  siteTimezone?: string | null;
  /** "See tokens" is on (Mumms admins): token split and cost columns. */
  showCost?: boolean;
};

/** Row class of a question that got no answer (greyed by ConversationDetail). */
export const UNANSWERED_ROW_CLASS = 'maia-unanswered';

const rowOf = (data: unknown) => data as ConversationQuestion | undefined;

/** The question on one line; one with no answer says why first. */
const rowQuestion = ({ data }: { data?: unknown }) => {
  const row = rowOf(data);
  const question = oneLine(row?.question);
  return row?.outcome
    ? fillTemplate(defaults.unansweredQuestion, {
        outcome: unansweredLabel(row.outcome),
        question,
      })
    : question;
};

const questionCells = (data: unknown): TokenCells | undefined => {
  const details = rowOf(data)?.details;
  return details
    ? {
        tokens: details.tokensUsed,
        input: details.inputTokens,
        cached: details.cachedTokens,
        output: details.outputTokens,
        candidates: details.candidatesTokens,
        thoughts: details.thoughtsTokens,
        questionsCost: details.questionsCostUsd,
        cacheCost: details.cacheCostUsd,
        totalCost: details.totalEstimatedCostUsd,
      }
    : undefined;
};

/** The view icon, except on a question with no answer: there is nothing to open. */
const questionViewColumn = (
  onOpenQuestion: (row: ConversationQuestion) => void,
): ColDef => {
  const column = viewColumn(defaults.viewQuestion, (data) =>
    onOpenQuestion(data as ConversationQuestion),
  );
  const renderIcon = column.cellRendererFramework as (
    params: ICellRendererParams,
  ) => JSX.Element | null;
  return {
    ...column,
    cellRendererFramework: (params: ICellRendererParams) =>
      rowOf(params.data)?.outcome ? null : renderIcon(params),
  };
};

/** Person, Topic and Model: the columns the question spans when expanded. */
const QUESTION_SPAN = 3;

const questionColumn: ColDef = {
  field: 'question',
  headerName: defaults.historyTopic,
  valueGetter: rowQuestion,
  tooltipValueGetter: rowQuestion,
};

/**
 * The questions of one conversation, oldest first, drawn under the
 * conversation row with no header of their own. Same colIds as the history
 * columns, in the same order, so ConversationDetail can give each one its
 * parent's width: Date (where its duration starts: the conversation's start for
 * the first question, else when it was asked; at the right edge, under the
 * conversation's time) · the question, across Person, Topic and Model (the tile has only
 * Topic) · Duration · the token columns · view (that question and its
 * answer, full size). A question that got no answer is a row too, in time
 * order, with why, what it spent, and no duration or view icon.
 */
export const getQuestionColumnDefs = ({
  onOpenQuestion,
  smallWidget,
  siteTimezone,
  showCost,
}: QuestionColumnDefsArgs): ColDef[] => [
  {
    colId: 'startedAt',
    field: 'startsAt',
    headerName: defaults.historyDate,
    sort: 'asc',
    comparator: isoComparator,
    // grid cells are flex boxes, so text-align does nothing: push the time
    // to the column's right edge, next to the question
    cellStyle: { display: 'flex', justifyContent: 'flex-end' },
    valueFormatter: ({ value }: ValueFormatterParams) =>
      formatTime(value, siteTimezone),
  },
  // a question row has no person or model of its own (they are the
  // conversation's), so the question runs from under Person to the end of
  // Model; the tile has neither column, so it takes Topic alone
  ...(smallWidget
    ? [{ ...questionColumn, colId: 'topic' }]
    : [
        { ...questionColumn, colId: 'person', colSpan: () => QUESTION_SPAN },
        { colId: 'topic', headerName: '' },
        { colId: 'model', headerName: '' },
      ]),
  // under the conversation's Messages (hidden in the tile, as it is there):
  // a question has no count of its own, but every column needs its twin so
  // the ones after it line up
  { colId: 'messages', headerName: '', hide: smallWidget },
  {
    colId: 'duration',
    headerName: defaults.historyDuration,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
    valueGetter: ({ data }: ValueGetterParams) =>
      rowOf(data)?.durationSeconds ?? null,
    valueFormatter: ({ value }: ValueFormatterParams) => formatDuration(value),
  },
  ...getTokenColumnDefs({ cells: questionCells, showCost, smallWidget }),
  questionViewColumn(onOpenQuestion),
];
